"use client"

import { FC, useRef } from "react"
import { Content } from "@prismicio/client"
import {
  PrismicRichText,
  PrismicText,
  SliceComponentProps,
} from "@prismicio/react"
import { PrismicNextImage } from "@prismicio/next"
import {
  LazyMotion,
  domAnimation,
  m,
  type Variants,
  useScroll,
  useTransform,
  useSpring,
} from "motion/react"
import { cn, sliceLabel } from "@/lib/utils"

export type FeatureGoalsGridProps =
  SliceComponentProps<Content.FeatureGoalsGridSlice>

const iconBgCycle = [
  "bg-orange-500",
  "bg-amber-500",
  "bg-blue-500",
  "bg-purple-500",
  "bg-green-500",
  "bg-rose-500",
]

const labelColorCycle = [
  "text-orange-600",
  "text-amber-600",
  "text-blue-600",
  "text-purple-600",
  "text-green-600",
  "text-rose-600",
]

// Staggered entrance per card — opacity + scale only (y handled by parallax MotionValue)
const vCardEntrance: Variants = {
  hidden: { opacity: 0, scale: 0.94 },
  visible: (i: number) => ({
    opacity: 1,
    scale: 1,
    transition: {
      duration: 0.55,
      delay: i * 0.09,
      ease: [0.22, 1, 0.36, 1] as const,
    },
  }),
}

// Spring-pop for labels, numbers and icons
const vLabelPop: Variants = {
  hidden: { opacity: 0, scale: 0.4 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: { type: "spring", stiffness: 360, damping: 14 },
  },
}

const FeatureGoalsGrid: FC<FeatureGoalsGridProps> = ({ slice }) => {
  const { primary } = slice
  const sectionRef = useRef<HTMLElement>(null)

  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start end", "end start"],
  })

  // Section header floats upward as you scroll deeper
  const headerY = useTransform(scrollYProgress, [0, 1], [30, -60])
  const smoothHeaderY = useSpring(headerY, { stiffness: 60, damping: 20 })

  // Progress bar grows left → right as section scrolls into view
  const progressScaleX = useTransform(scrollYProgress, [0.05, 0.85], [0, 1])

  const sectionBg =
    primary.background_color === "Yellow" ? "bg-sacred-cream" : "bg-white"

  const cardBg =
    primary.goal_item_background_color === "Yellow"
      ? "bg-orange-50 border-orange-100"
      : "bg-white border-gray-100"

  const gridCols =
    primary.grid === "4"
      ? "sm:grid-cols-2 lg:grid-cols-4"
      : primary.grid === "3"
        ? "sm:grid-cols-2 lg:grid-cols-3"
        : "sm:grid-cols-2"

  const alignClass = primary.goal_item_align
    ? "items-start text-left"
    : "items-center text-center"

  return (
    <LazyMotion features={domAnimation}>
      <section
        data-slice-type={slice.slice_type}
        data-slice-variation={slice.variation}
        ref={sectionRef}
        aria-label={sliceLabel(slice.slice_type)}
        className={cn("relative overflow-hidden section-py", sectionBg)}
      >
        {/* Scroll progress bar — red→orange→gold, grows across the top */}
        <m.div
          aria-hidden
          className="absolute inset-x-0 top-0 h-0.5 origin-left"
          style={{
            scaleX: progressScaleX,
            background:
              "linear-gradient(to right, var(--sacred-red), var(--sacred-orange), var(--sacred-gold))",
          }}
        />

        <div className="container flex flex-col gap-10">
          {/* Section header — parallax upward drift */}
          {primary.section_title_description && (
            <m.div
              className="mx-auto max-w-2xl text-center"
              style={{ y: smoothHeaderY }}
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] as const }}
            >
              <PrismicRichText
                field={primary.section_title_description}
                components={{
                  heading1: ({ children }) => (
                    <h1 className="text-sacred-red">{children}</h1>
                  ),
                  heading2: ({ children }) => (
                    <h2 className="text-sacred-red">{children}</h2>
                  ),
                  heading3: ({ children }) => (
                    <h3 className="text-sacred-red">{children}</h3>
                  ),
                  paragraph: ({ children }) => (
                    <p className="text-muted-foreground">{children}</p>
                  ),
                }}
              />
            </m.div>
          )}

          {/* Grid */}
          {primary.goal_items && primary.goal_items.length > 0 && (
            <div className={cn("grid grid-cols-1 gap-6", gridCols)}>
              {primary.goal_items.map((item, i) => (
                <m.div
                  key={i}
                  custom={i}
                  variants={vCardEntrance}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true, margin: "-60px" }}
                  className={cn(
                    "flex flex-col gap-3 rounded-2xl border p-6 shadow-sm transition-shadow hover:shadow-md",
                    cardBg,
                    alignClass,
                  )}
                >
                  {/* Icon — spring-pops in */}
                  {item.icon?.url && (
                    <m.div
                      initial="hidden"
                      whileInView="visible"
                      viewport={{ once: true, margin: "-40px" }}
                      variants={vLabelPop}
                      className={cn(
                        "flex size-12 items-center justify-center rounded-2xl",
                        primary.goal_item_style
                          ? iconBgCycle[i % iconBgCycle.length]
                          : "",
                      )}
                    >
                      <PrismicNextImage
                        field={item.icon}
                        fallbackAlt=""
                        width={32}
                        height={32}
                        className="size-8 object-contain brightness-200"
                        imgixParams={{ auto: ["format", "compress"], q: 75 }}
                      />
                    </m.div>
                  )}
                  {!item.icon?.url && item.order_number && (
                    <m.span
                      initial="hidden"
                      whileInView="visible"
                      viewport={{ once: true, margin: "-40px" }}
                      variants={vLabelPop}
                      className={cn(
                        "flex aspect-square size-10 items-center justify-center rounded-full bg-sacred-orange text-lg font-bold text-white md:text-xl",
                      )}
                    >
                      {item.order_number}
                    </m.span>
                  )}

                  {/* Label — spring-pops in */}
                  {item.label && (
                    <m.span
                      initial="hidden"
                      whileInView="visible"
                      viewport={{ once: true, margin: "-40px" }}
                      variants={vLabelPop}
                      className={cn(
                        "text-lg font-normal text-sacred-orange md:text-xl",
                      )}
                    >
                      {item.label}
                    </m.span>
                  )}

                  {/* Title */}
                  {item.title && (
                    <span
                      className={cn(
                        "text-xl font-semibold md:text-2xl xl:text-3xl",
                        primary.goal_item_style
                          ? labelColorCycle[i % labelColorCycle.length]
                          : "text-sacred-red",
                      )}
                    >
                      <PrismicText field={item.title} />
                    </span>
                  )}

                  {/* Description */}
                  {item.description && (
                    <div>
                      <PrismicRichText
                        field={item.description}
                        components={{
                          paragraph: ({ children }) => (
                            <p className="text-sm/relaxed text-muted-foreground">
                              {children}
                            </p>
                          ),
                          strong: ({ children }) => (
                            <b className="mb-0 text-black">{children}</b>
                          ),
                        }}
                      />
                    </div>
                  )}
                </m.div>
              ))}
            </div>
          )}
        </div>
      </section>
    </LazyMotion>
  )
}

export default FeatureGoalsGrid
